// External library imports
import { ReactElement, Suspense, lazy, useMemo, useRef } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'

// Internal library/module imports
import { ModalHeader } from 'syf-component-library/ui/patterns/SlideInModalPortal'
import {
  AuthenticationRedirectProps,
  handleMFEAuthenticationRedirect
} from 'helpers/authenticationRedirect'
import getMFEConfig from 'helpers/getMFEConfig'
import triggerAnalyticsEvent from 'helpers/triggerAnalyticsEvent'
import { createOrChangePinModalContainerId } from 'const/modalContainerId'
import routes from 'const/routes'
import { selectedAccountAtom } from 'store/atoms'
import { useAccountFeatures, useAccountSummary } from 'const/syfApiQueries'
import { ACCOUNT_DELINQUENT } from 'const/account/conditions'
import { PSCC, RC } from 'const/account/products'
import Loader from 'authorization-module/lib/ui/atoms/Loader'
import { getBrandRefreshPhase } from 'helpers/getBrandRefreshPhase'
import i18n from 'strings/i18n'
import { SlideInModalPortalWrapper } from 'ui/patterns/SlideInModalPortalWrapper'

const ChangePinMFE = lazy(() => import('@card-actions/change-pin'))

const CreateOrChangePinModal = (): ReactElement => {
  const selectedAccount = useAtomValue(selectedAccountAtom)
  const headerRef = useRef(null)
  const navigate = useNavigate()

  const { data: accountFeatures, isLoading: isAccountFeaturesLoading } =
    useAccountFeatures(selectedAccount)
  const {
    data: { conditions = [], product } = {},
    isError: isAccountSummaryError
  } = useAccountSummary(selectedAccount)

  const isDelinquent = useMemo(() => {
    return conditions?.includes(ACCOUNT_DELINQUENT)
  }, [conditions])

  const isRevolving = useMemo(
    () => [RC, PSCC].includes(product?.lineOfBusiness),
    [product]
  )
  const containerId = createOrChangePinModalContainerId(selectedAccount)

  const handleCloseModal = () => {
    document.getElementById('change-pin-link')?.focus()
    navigate(routes.ACCOUNT_SERVICES)
  }

  const handleAuthenticationRedirect = () => {
    const props: AuthenticationRedirectProps = {
      navigateTo: routes.CHANGE_PIN,
      modal: 'CreateOrChangePin',
      scope: 'read write write:sensitive'
    }

    handleMFEAuthenticationRedirect(props)
  }

  if (isAccountFeaturesLoading) {
    return <Loader />
  }

  if (
    isAccountSummaryError ||
    !accountFeatures?.cashAdvancePinEnabled ||
    !isRevolving ||
    isDelinquent
  ) {
    return <Navigate to={routes.ERROR} />
  }

  return (
    <SlideInModalPortalWrapper
      ariaLabel="create/change pin"
      containerId={containerId}
      escapeModalHandler={handleCloseModal}
      id={`change-pin-slide-in-modal-${selectedAccount}`}
      isOpen
      modalTriggerId="change-pin-link"
      header={
        <ModalHeader
          closeModal={handleCloseModal}
          content={i18n({ button: 'createChangePIN' }) as string}
          data-cy="pin-change-drawer-name"
          data-test="pin-change-modal-header"
          dataReason="x button"
          dataType="change pin"
          headerRef={headerRef}
        />
      }
    >
      <Suspense fallback={<Loader />}>
        <ChangePinMFE
          onCloseModal={handleCloseModal}
          mfeConfig={getMFEConfig('mfe')}
          featureFlags={{
            brandRefreshPhase: getBrandRefreshPhase()
          }}
          selectedAccountId={selectedAccount}
          triggerAnalyticsEvent={triggerAnalyticsEvent}
          authenticationRedirect={handleAuthenticationRedirect}
        />
      </Suspense>
    </SlideInModalPortalWrapper>
  )
}

export default CreateOrChangePinModal
